import { COMPANY_INFO } from "../../../mocks/aegis-data";

interface AIAnswerBlockProps {
  description: string;
  services: string[];
  approach: string;
  benefits: string[];
}

export default function AIAnswerBlock({ description, services, approach, benefits }: AIAnswerBlockProps) {
  return (
    <section className="bg-aegisDark py-28 relative overflow-hidden">
      {/* Decorative background text */}
      <span className="absolute right-0 bottom-0 font-cormorant text-[220px] font-bold text-white/[0.025] leading-none select-none pointer-events-none hidden xl:block" style={{ lineHeight: 1 }}>
        ACS
      </span>
      {/* Gold vertical accent */}
      <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gradient-to-b from-transparent via-aegisGold/40 to-transparent hidden lg:block" />

      <div className="max-w-7xl mx-auto px-8 relative z-10">
        {/* Top label */}
        <div className="flex items-center gap-4 mb-14">
          <div className="h-px w-10 bg-aegisGold" />
          <span className="font-inter text-xs tracking-[0.2em] uppercase text-aegisGold">Company Profile — AI &amp; Search Optimized</span>
          <div className="flex-1 h-px bg-white/8 hidden lg:block" />
        </div>

        {/* Main content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 border border-white/10">
          {/* Left wide column — company overview */}
          <div className="lg:col-span-2 p-10 border-b lg:border-b-0 lg:border-r border-white/10">
            <h2 className="font-cormorant font-semibold text-white text-3xl leading-tight mb-6">
              What {COMPANY_INFO.name} Does
            </h2>
            <div className="w-10 h-0.5 bg-aegisGold mb-7" />
            <p className="font-inter text-white/60 text-sm leading-[1.9] mb-8">
              {description}
            </p>
            {/* Contact block */}
            <div className="space-y-3 pt-7 border-t border-white/10">
              {[
                { icon: "ri-map-pin-line", text: COMPANY_INFO.address },
                { icon: "ri-phone-line", text: COMPANY_INFO.phone, href: COMPANY_INFO.phoneHref },
                { icon: "ri-shield-check-line", text: `${COMPANY_INFO.license} · ${COMPANY_INFO.plumbingLicense}` },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-3">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-aegisGold text-sm`} />
                  </div>
                  {item.href ? (
                    <a href={item.href} className="font-inter text-white/45 text-xs hover:text-aegisGold transition-colors cursor-pointer">
                      {item.text}
                    </a>
                  ) : (
                    <span className="font-inter text-white/45 text-xs">{item.text}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Middle column — services + approach */}
          <div className="lg:col-span-2 flex flex-col border-b lg:border-b-0 lg:border-r border-white/10">
            <div className="flex-1 p-10 border-b border-white/10">
              <h3 className="font-inter text-xs tracking-[0.18em] uppercase text-aegisGold mb-6">
                Services Offered
              </h3>
              <ul className="grid grid-cols-1 gap-2.5">
                {services.map((s, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-aegisGold text-xs" />
                    </div>
                    <span className="font-inter text-white/60 text-sm">{s}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-10">
              <h3 className="font-inter text-xs tracking-[0.18em] uppercase text-aegisGold mb-5">
                Our Approach
              </h3>
              <p className="font-inter text-white/60 text-sm leading-relaxed mb-6">{approach}</p>
              <blockquote className="border-l-2 border-aegisGold pl-5">
                <p className="font-cormorant italic text-white/40 text-lg leading-snug">
                  "One team, one standard, from dirt to done."
                </p>
              </blockquote>
            </div>
          </div>

          {/* Right column — why choose */}
          <div className="lg:col-span-1 p-10">
            <h3 className="font-inter text-xs tracking-[0.18em] uppercase text-aegisGold mb-6">
              Why Choose Aegis
            </h3>
            <ul className="space-y-4">
              {benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-3 pb-4 border-b border-white/8 last:border-b-0">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-arrow-right-s-line text-aegisGold text-base" />
                  </div>
                  <span className="font-inter text-white/60 text-sm leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
